import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { NavigationScreenState } from '@gitbook/app-navigation';
import { localStorage } from '@gitbook/util-local-storage';

import { apiQuery } from '../state';
import { NavigationFunctionsContext } from './contexts';
import { HistoryRedirect } from './history';
import { LAST_SEEN_CONTENT_KEY } from './NavigationProvider';
import type { NavigationState, NavigationTarget } from './types';

/**
 * Redirect to another screen.
 * This component is optimized compared to a navigate call in useEffect to skip the rendering.
 */
export function NavigationRedirect(props: {
    to: NavigationTarget;
    replace?: boolean;
}): React.ReactElement {
    const { to, replace = true } = props;
    const { getLocationFor } = React.useContext(NavigationFunctionsContext);

    return <HistoryRedirect to={getLocationFor(to)} push={!replace} />;
}

/**
 * Render a screen only if it has an organization set. Otherwise, redirect to the first organization.
 */
export function NavigationScreenWithOrganization<
    Screen extends Extract<NavigationScreenState, { organization?: string }>,
>(props: {
    screen: Screen;
    children: (props: { screen: Screen & { organization: string } }) => React.ReactElement;
}) {
    const { screen, children } = props;

    if (screen.organization) {
        return children({ screen: screen as Screen & { organization: string } });
    }

    // First try to redirect the user to the organization from the last visited content if any
    // otherwise fallback to the first organization in its list of organizations.
    const lastContent = localStorage.get<NavigationState>(LAST_SEEN_CONTENT_KEY);
    const lastVisitedScreen = lastContent?.screen;
    const lastVisitedOrganization =
        lastVisitedScreen && 'organization' in lastVisitedScreen && lastVisitedScreen.organization
            ? lastVisitedScreen.organization
            : undefined;

    return lastVisitedOrganization ? (
        <NavigationRedirect
            to={{
                screen: {
                    ...screen,
                    organization: lastVisitedOrganization,
                },
            }}
        />
    ) : (
        <TopToOrgLevelRedirectFromUserOrgs screen={screen} />
    );
}

function TopToOrgLevelRedirectFromUserOrgs(props: {
    screen: Extract<NavigationScreenState, { organization?: string }>;
}): React.ReactElement {
    const { screen } = props;
    const {
        data: { items: organizations },
    } = useSuspenseQuery(apiQuery('listOrganizationsForAuthenticatedUser', { limit: 1 }));

    if (organizations.length === 0) {
        return (
            <NavigationRedirect
                to={{
                    screen: {
                        type: 'welcome',
                    },
                    overlay: null,
                }}
            />
        );
    }

    return (
        <NavigationRedirect
            to={{
                screen: {
                    ...screen,
                    organization: organizations[0].id,
                },
            }}
        />
    );
}
